import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DataTable, { type Column } from "../components/DataTable";
import "../styles/adminMarketPriceAnalysis.css";

type ProductSearchOption = {
  id: number;
  productName: string;
  SKU?: string;
  model?: string;
  barcode?: string;
  activePrice?: string;
  stock_status?: string;
  productPrice?: number;
};

type MarketResult = {
  id?: number;
  title?: string;

  sellerName?: string;
  seller_name?: string;

  domain?: string;
  url?: string;

  priceValue?: number;
  price_value?: number;

  currency?: string;

  rating?: number | null;

  reviewsCount?: number | null;
  reviews_count?: number | null;

  rankPosition?: number | null;
  rank_position?: number | null;

  matchScore?: number;
  match_score?: number;

  matchType?: "Exact" | "Similar" | "Weak" | "Unmatched";
  match_type?: "Exact" | "Similar" | "Weak" | "Unmatched";
};

type NormalizedMarketResult = {
  id?: number;
  title: string;
  sellerName: string;
  domain: string;
  url: string;
  priceValue: number | null;
  currency: string;
  rating: number | null;
  reviewsCount: number | null;
  rankPosition: number | null;
  matchScore: number;
  matchType: "Exact" | "Similar" | "Weak" | "Unmatched";
};

type MarketAnalysisResponse = {
  runId: number;
  product: {
    id: number;
    productName: string;
    productPrice: number;
    SKU?: string;
    model?: string;
    activePrice?: string;
  };
  searchKeyword: string;
  lyrazonePrice: number;
  lowestPrice: number | null;
  averagePrice: number | null;
  highestPrice: number | null;
  resultCount: number;
  recommendation: string;
  results: MarketResult[];
};

const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000";

const AdminMarketPriceAnalysis: React.FC = () => {
  const [productQuery, setProductQuery] = useState("");
  const [selectedProduct, setSelectedProduct] =
    useState<ProductSearchOption | null>(null);
  const [productOptions, setProductOptions] = useState<ProductSearchOption[]>(
    [],
  );
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [productSearching, setProductSearching] = useState(false);

  const productSearchRef = useRef<HTMLDivElement | null>(null);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<MarketAnalysisResponse | null>(null);
  const [error, setError] = useState("");

  const fetchProductOptions = async (query: string) => {
    try {
      setProductSearching(true);

      const response = await axios.get(
        `${API_BASE_URL}/admin/market-price/products/search`,
        {
          params: {
            query,
          },
        },
      );

      if (response.data?.success) {
        setProductOptions(response.data.data || []);
      }
    } catch (err) {
      console.error("Product search error:", err);
    } finally {
      setProductSearching(false);
    }
  };

  React.useEffect(() => {
    const delay = setTimeout(() => {
      fetchProductOptions(productQuery.trim());
    }, 350);

    return () => clearTimeout(delay);
  }, [productQuery]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        productSearchRef.current &&
        !productSearchRef.current.contains(event.target as Node)
      ) {
        setProductDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const formatMoney = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "-";
    }

    return `RM ${Number(value).toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const normalizeMarketResults = (
    results: MarketResult[] = [],
  ): NormalizedMarketResult[] => {
    return results.map((item) => ({
      id: item.id,
      title: item.title || "-",
      sellerName: item.sellerName || item.seller_name || "-",
      domain: item.domain || "-",
      url: item.url || "",
      priceValue:
        item.priceValue !== undefined && item.priceValue !== null
          ? Number(item.priceValue)
          : item.price_value !== undefined && item.price_value !== null
            ? Number(item.price_value)
            : null,
      currency: item.currency || "MYR",
      rating:
        item.rating !== undefined && item.rating !== null
          ? Number(item.rating)
          : null,
      reviewsCount:
        item.reviewsCount !== undefined && item.reviewsCount !== null
          ? Number(item.reviewsCount)
          : item.reviews_count !== undefined && item.reviews_count !== null
            ? Number(item.reviews_count)
            : null,
      rankPosition:
        item.rankPosition !== undefined && item.rankPosition !== null
          ? Number(item.rankPosition)
          : item.rank_position !== undefined && item.rank_position !== null
            ? Number(item.rank_position)
            : null,
      matchScore:
        item.matchScore !== undefined && item.matchScore !== null
          ? Number(item.matchScore)
          : item.match_score !== undefined && item.match_score !== null
            ? Number(item.match_score)
            : 0,
      matchType: item.matchType || item.match_type || "Unmatched",
    }));
  };

  const marketResultRows = useMemo(() => {
    return normalizeMarketResults(analysis?.results || []);
  }, [analysis]);

  const marketResultColumns: Column[] = useMemo(
    () => [
      {
        key: "rowNo",
        label: "#",
        filterable: false,
        render: (_row, rowIndex) => rowIndex + 1,
      },
      {
        key: "title",
        label: "Market Product",
      },
      {
        key: "sellerName",
        label: "Seller",
      },
      {
        key: "priceValue",
        label: "Price",
        render: (row: NormalizedMarketResult) => formatMoney(row.priceValue),
      },
      {
        key: "matchType",
        label: "Match",
        render: (row: NormalizedMarketResult) => (
          <span
            className={`admin-market-price-badge ${String(
              row.matchType || "Unmatched",
            ).toLowerCase()}`}
          >
            {row.matchType || "Unmatched"}
          </span>
        ),
      },
      {
        key: "matchScore",
        label: "Score",
        render: (row: NormalizedMarketResult) => `${row.matchScore || 0}%`,
      },
      {
        key: "rating",
        label: "Rating",
        render: (row: NormalizedMarketResult) =>
          row.rating ? `${row.rating} (${row.reviewsCount || 0})` : "-",
      },
      {
        key: "domain",
        label: "Source",
        render: (row: NormalizedMarketResult) =>
          row.url ? (
            <a
              href={row.url}
              target="_blank"
              rel="noreferrer"
              className="admin-market-price-source-link"
              onClick={(event) => event.stopPropagation()}
            >
              View
            </a>
          ) : (
            row.domain || "-"
          ),
      },
    ],
    [],
  );

  const handleAnalyze = async () => {
    if (!selectedProduct?.id) {
      setError("Please select a product.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setAnalysis(null);

      const payload = searchKeyword.trim()
        ? { searchKeyword: searchKeyword.trim() }
        : {};

      const response = await axios.post(
        `${API_BASE_URL}/admin/market-price/analyze/${selectedProduct.id}`,
        payload,
      );

      if (response.data?.success) {
        setAnalysis(response.data.data);
      } else {
        setError(response.data?.message || "Market analysis failed.");
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err.message ||
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getPricePositionText = () => {
    if (!analysis?.lyrazonePrice || !analysis?.averagePrice) return "-";

    const diff =
      ((Number(analysis.lyrazonePrice) - Number(analysis.averagePrice)) /
        Number(analysis.averagePrice)) *
      100;

    if (diff > 0) return `${diff.toFixed(2)}% above market average`;
    if (diff < 0) return `${Math.abs(diff).toFixed(2)}% below market average`;

    return "Same as market average";
  };

  return (
    <div className="admin-market-price-page">
      <div className="admin-market-price-header">
        <div>
          <h2>Market Price Analysis</h2>
          <p>
            Compare LyraZone product prices against Malaysia market pricing.
          </p>
        </div>
      </div>

      <div className="admin-market-price-panel">
        <div className="admin-market-price-form-grid">
          <div
            className="admin-market-price-field admin-market-price-product-search-field"
            ref={productSearchRef}
          >
            <label>Select Product</label>

            <div className="admin-market-price-product-search-wrap">
              <input
                value={productQuery}
                onChange={(e) => {
                  setProductQuery(e.target.value);
                  setSelectedProduct(null);
                  setProductDropdownOpen(true);
                }}
                onFocus={() => {
                  setProductDropdownOpen(true);
                  if (productOptions.length === 0) {
                    fetchProductOptions(productQuery.trim());
                  }
                }}
                placeholder="Search product by name, SKU, model or barcode"
              />

              {selectedProduct && (
                <button
                  type="button"
                  className="admin-market-price-clear-product"
                  onClick={() => {
                    setSelectedProduct(null);
                    setProductQuery("");
                    setProductOptions([]);
                    setAnalysis(null);
                  }}
                >
                  ×
                </button>
              )}

              {productDropdownOpen && (
                <div className="admin-market-price-product-dropdown">
                  {productSearching ? (
                    <div className="admin-market-price-product-option muted">
                      Searching products...
                    </div>
                  ) : productOptions.length === 0 ? (
                    <div className="admin-market-price-product-option muted">
                      No products found.
                    </div>
                  ) : (
                    productOptions.map((product) => (
                      <button
                        type="button"
                        key={product.id}
                        className="admin-market-price-product-option"
                        onClick={() => {
                          setSelectedProduct(product);
                          setProductQuery(product.productName);
                          setProductDropdownOpen(false);
                        }}
                      >
                        <div>
                          <strong>{product.productName}</strong>
                          <span>
                            ID: {product.id}
                            {product.SKU ? ` | SKU: ${product.SKU}` : ""}
                            {product.model ? ` | Model: ${product.model}` : ""}
                          </span>
                        </div>

                        <small>
                          RM{" "}
                          {Number(product.productPrice || 0).toLocaleString(
                            "en-MY",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </small>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {selectedProduct && (
              <div className="admin-market-price-selected-product">
                Selected: #{selectedProduct.id} — {selectedProduct.productName}
              </div>
            )}
          </div>

          <div className="admin-market-price-field">
            <label>Search Keyword Optional</label>
            <input
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="Example: TSC TX610 600dpi Barcode Printer Malaysia"
            />
          </div>

          <button
            className="admin-market-price-btn"
            onClick={handleAnalyze}
            disabled={loading}
          >
            {loading ? "Analyzing..." : "Analyze Market Price"}
          </button>
        </div>

        {error && <div className="admin-market-price-error">{error}</div>}
      </div>

      {analysis && (
        <>
          <div className="admin-market-price-summary">
            <div className="admin-market-price-card">
              <span>LyraZone Price</span>
              <strong>{formatMoney(analysis.lyrazonePrice)}</strong>
              <small>Active product price</small>
            </div>

            <div className="admin-market-price-card">
              <span>Lowest Market Price</span>
              <strong>{formatMoney(analysis.lowestPrice)}</strong>
              <small>Based on matched results</small>
            </div>

            <div className="admin-market-price-card">
              <span>Average Market Price</span>
              <strong>{formatMoney(analysis.averagePrice)}</strong>
              <small>{getPricePositionText()}</small>
            </div>

            <div className="admin-market-price-card">
              <span>Highest Market Price</span>
              <strong>{formatMoney(analysis.highestPrice)}</strong>
              <small>{analysis.resultCount} result(s) matched</small>
            </div>
          </div>

          <div className="admin-market-price-product-box">
            <h3>{analysis.product.productName}</h3>

            <p>
              <b>Search Keyword:</b> {analysis.searchKeyword}
            </p>

            <p>
              <b>Recommendation:</b> {analysis.recommendation}
            </p>
          </div>

          <div className="admin-market-price-results-section">
            <div className="admin-market-price-results-header">
              <h3>Market Comparison Results</h3>
              <span>{marketResultRows.length} Result(s)</span>
            </div>

            {/* Desktop Table */}
            <div className="admin-market-price-desktop-table">
              <DataTable
                columns={marketResultColumns}
                data={marketResultRows}
                getRowKey={(row, index) =>
                  row.id || `${row.title}-${row.sellerName}-${index}`
                }
              />
            </div>

            {/* Mobile Cards */}
            <div className="admin-market-price-mobile-cards">
              {marketResultRows.length === 0 ? (
                <div className="admin-market-price-mobile-empty">
                  No market comparison results found.
                </div>
              ) : (
                marketResultRows.map((row, index) => (
                  <div
                    className="admin-market-price-mobile-card"
                    key={row.id || `${row.title}-${row.sellerName}-${index}`}
                  >
                    <div className="admin-market-price-mobile-status">
                      <span
                        className={`admin-market-price-mobile-status-badge ${String(
                          row.matchType || "Unmatched",
                        ).toLowerCase()}`}
                      >
                        {row.matchType || "Unmatched"}
                      </span>
                    </div>

                    <div className="admin-market-price-mobile-field">
                      <span>Market Product</span>
                      <strong>{row.title || "-"}</strong>
                    </div>

                    <div className="admin-market-price-mobile-field">
                      <span>Seller</span>
                      <strong>{row.sellerName || "-"}</strong>
                    </div>

                    <div className="admin-market-price-mobile-info-row">
                      <div className="admin-market-price-mobile-info-item">
                        <span>Price</span>
                        <strong>{formatMoney(row.priceValue)}</strong>
                      </div>

                      <div className="admin-market-price-mobile-info-item">
                        <span>Match Score</span>
                        <strong>{row.matchScore || 0}%</strong>
                      </div>

                      <div className="admin-market-price-mobile-info-item">
                        <span>Rating</span>
                        <strong>
                          {row.rating
                            ? `${row.rating} (${row.reviewsCount || 0})`
                            : "-"}
                        </strong>
                      </div>
                    </div>

                    {row.url ? (
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noreferrer"
                        className="admin-market-price-mobile-view-btn"
                        onClick={(event) => event.stopPropagation()}
                      >
                        View Source
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="admin-market-price-mobile-view-btn disabled"
                        disabled
                      >
                        No Source
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminMarketPriceAnalysis;
